// useApi.js
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const PRIMARY_BASE = 'https://orca-app-mna7u.ondigitalocean.app';
const FALLBACK_BASE = 'https://rl-core-app-kpx3b.ondigitalocean.app';
//const PRIMARY_BASE = 'http://localhost:5000';

function isNetworkError(error) {
  return !error.response && (error.code === 'ECONNABORTED' || error.message === 'Network Error');
}

/**
 * Login with automatic failover: tries RL_API first, falls back to RL_Core
 * if RL_API is unreachable (network error / timeout).
 */
export async function loginWithFallback({ login_value, password, userType }) {
  try {
    const response = await axios.post(
      `${PRIMARY_BASE}/user/login`,
      { login_value, password, userType },
      { timeout: 10000 }
    );
    return response;
  } catch (error) {
    if (isNetworkError(error)) {
      console.warn('RL_API unreachable for login, falling back to RL_Core');
      const fallbackResponse = await axios.post(
        `${FALLBACK_BASE}/user/login`,
        { login_value, password, userType },
        { timeout: 10000 }
      );
      return fallbackResponse;
    }
    throw error;
  }
}

const useApi = () => {
  const navigate = useNavigate();

  // Handling session expiry and redirection
  function handleSessionExpiry() {
    // Clear token from local storage
    localStorage.removeItem('accessToken');
    const userType = localStorage.getItem('userType');
    localStorage.removeItem('userType');

    // Decide where to redirect based on user type
    if (userType === 'parent') {
      navigate('/ParentLogin');
    } else if (userType === 'student') {
      navigate('/StudentLogin');
    } else {
      // Default case if user type is unknown or not set
      navigate('/');  // Redirect to a default location, e.g., homepage or common login page
    }
  }

  async function refreshAccessToken() {
    const refreshToken = localStorage.getItem('refreshToken');

    // Try RL_API first
    try {
      const response = await axios.post(
        `${PRIMARY_BASE}/user/token/refresh`,
        {},
        { headers: { 'Authorization': `Bearer ${refreshToken}` }, timeout: 10000 }
      );
      const { access_token } = response.data;
      localStorage.setItem('accessToken', access_token);
      return access_token;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('RL_API unreachable for token refresh, falling back to RL_Core');
      } else {
        console.error('Refresh token failed:', error);
        return null;
      }
    }

    // Fallback to RL_Core
    try {
      const response = await axios.post(
        `${FALLBACK_BASE}/user/token/refresh`,
        {},
        { headers: { 'Authorization': `Bearer ${refreshToken}` }, timeout: 10000 }
      );
      const { access_token } = response.data;
      localStorage.setItem('accessToken', access_token);
      return access_token;
    } catch (fallbackError) {
      console.error('Fallback refresh token also failed:', fallbackError);
      return null;
    }
  }

  // Create axios instance
  const api = axios.create({
    baseURL: PRIMARY_BASE,
  });

  // Set the Authorization header dynamically for each request
  api.interceptors.request.use(config => {
    const token = localStorage.getItem('accessToken');
    config.headers.Authorization = token ? `Bearer ${token}` : '';
    return config;
  });

  api.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config;
      if (error.response && error.response.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        const newAccessToken = await refreshAccessToken();
        if (newAccessToken) {
          originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        }
        // Only reject the Promise here, let the component handle what happens next
      }
      return Promise.reject(error);
    }
  );

  return api;
}

export default useApi;
